<!-- SearchSubscriptionInput.vue -->
<script setup>
import { reactive, watch, onUnmounted } from "vue";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import debounce from "lodash.debounce";

// ✅ NOVO: prop que controla visibilidade do campo ID Usuário
// false  → usuário comum (só vê as próprias assinaturas; campo oculto e imutável)
// true   → Sys (vê tudo; campo visível e funcional)
defineProps({
  isSys: {
    type: Boolean,
    default: false,
  },
});

const store = useSubscriptionStore();

// ✅ Estado local espelhando store.filters (inicializa com o que já estiver na store)
const local = reactive({
  subscriptionId: store.filters.subscriptionId,
  userId:         store.filters.userId,
  userName:       store.filters.userName,
  planName:  store.filters.planName,
  paymentMethod:  store.filters.paymentMethod,
  status:         store.filters.status,
  paymentStatus:  store.filters.paymentStatus,
});

// ✅ MUDANÇA: todos os campos agora disparam refetch no backend via store.setFilter.
// Campos numéricos usam debounce + validação de inteiro positivo.
// Campos de texto usam debounce simples.
// Selects disparam imediatamente (sem debounce).
// O getter filteredSubscriptions mantém filtragem local instantânea enquanto o
// debounce está em voo, evitando piscar na tabela.

// ✅ Campos numéricos: debounce + validação de inteiro positivo
const debouncedNumeric = debounce((field, value) => {
  const trimmed = String(value ?? "").trim();
  // Permite vazio (limpar filtro) ou inteiros positivos
  if (trimmed !== "" && (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0)) return;
  store.setFilter(field, trimmed);
}, 400);

// ✅ Campos texto: debounce simples
const debouncedText = debounce((field, value) => {
  store.setFilter(field, String(value ?? "").trim());
}, 300);

// Watch individual por campo para controle fino de debounce
watch(() => local.subscriptionId, (v) => debouncedNumeric("subscriptionId", v));
watch(() => local.userId,         (v) => debouncedNumeric("userId", v));
watch(() => local.userName,       (v) => debouncedText("userName", v));
watch(() => local.planName,    (v) => debouncedText("planName", v));    // ✅ MUDANÇA: era planId/debouncedNumeric
watch(() => local.paymentMethod,  (v) => debouncedText("paymentMethod", v));
watch(() => local.status,         (v) => store.setFilter("status", v));         // select: sem debounce
watch(() => local.paymentStatus,  (v) => store.setFilter("paymentStatus", v));  // select: sem debounce

function clearAll() {
  local.subscriptionId = "";
  local.userId         = "";
  local.userName       = "";
  local.planName       = "";
  local.paymentMethod  = "";
  local.status         = "";
  local.paymentStatus  = "";
  store.clearFilters();
}

const hasActiveFilters = () =>
  Object.values(local).some((v) => String(v ?? "").trim() !== "");

onUnmounted(() => {
  store.clearFilters();
});

</script>

<template>
  <div class="filter-panel">
    <div class="filter-row">
      <!-- ID Assinatura -->
      <div class="filter-field">
        <label class="filter-label">ID Assin.</label>
        <input
          v-model="local.subscriptionId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="ex: 42"
          class="filter-input"
        />
      </div>

      <!-- ID Usuário: visível apenas para Sys -->
      <!-- ✅ NOVO: v-if="isSys" — não-Sys não vê nem pode alterar este filtro -->
      <div v-if="isSys" class="filter-field">
        <label class="filter-label">ID Usuário</label>
        <input
          v-model="local.userId"
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"
          placeholder="ex: 7"
          class="filter-input"
        />
      </div>

      <!-- Nome Usuário: visível apenas para Sys -->
      <div v-if="isSys" class="filter-field">
        <label class="filter-label">Nome Usuário</label>
        <input
          v-model="local.userName"
          type="text"
          placeholder="ex: Maria"
          class="filter-input"
        />
      </div>

      <!-- Plano -->
      <!-- ✅ MUDANÇA: era "ID Plano" (numérico); agora é "Plano" (texto livre, busca por nome) -->
      <div class="filter-field">
        <label class="filter-label">Plano</label>
        <input
          v-model="local.planName"
          type="text"
          placeholder="ex: Pro, Free…"
          class="filter-input"
        />
      </div>

      <!-- Método -->
      <div class="filter-field">
        <label class="filter-label">Método</label>
        <input
          v-model="local.paymentMethod"
          type="text"
          placeholder="pix, credit_card…"
          class="filter-input"
        />
      </div>

      <!-- Status assinatura -->
      <div class="filter-field">
        <label class="filter-label">Status</label>
        <select v-model="local.status" class="filter-input filter-select">
          <option value="">Todos</option>
          <option value="active">Ativa</option>
          <option value="cancelled">Cancelada</option>
          <option value="expired">Expirada</option>
          <option value="upgraded">Atualizada</option>
          <option value="pendingpayment">Aguardando</option>
        </select>
      </div>

      <!-- Status pagamento -->
      <div class="filter-field">
        <label class="filter-label">Pagamento</label>
        <select v-model="local.paymentStatus" class="filter-input filter-select">
          <option value="">Todos</option>
          <option value="approved">Aprovado</option>
          <option value="pending">Pendente</option>
          <option value="rejected">Rejeitado</option>
          <option value="cancelled">Cancelado</option>
          <option value="refunded">Estornado</option>
          <option value="refundfailed">Revisar estorno</option>
          <option value="none">—</option>
        </select>
      </div>

      <!-- Botão limpar: label + ícone -->
      <div v-if="hasActiveFilters()" class="filter-field filter-field-clear">
        <label class="filter-label">&nbsp;</label>
        <button
          class="filter-clear"
          title="Limpar filtros"
          @click="clearAll"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" class="clear-icon">
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
          <span>Limpar</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.filter-panel {
  width: 100%;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: flex-end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 160px;
}

.filter-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--text-light);
  white-space: nowrap;
  padding-left: 2px;
}

.filter-input {
  width: 100%;
  height: 32px;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.8rem;
  border-radius: 6px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.filter-input:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.filter-select {
  cursor: pointer;
  /* ✅ MUDANÇA: appearance: none remove a seta nativa do browser
     (que somava com a seta do framework/Tailwind, gerando duas setas).
     A seta abaixo é adicionada via background-image para ter apenas uma. */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 20 20' fill='%236b7280'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.45rem center;
  background-size: 18px 18px;
  padding-right: 1.6rem;
}

.filter-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  /* ✅ MUDANÇA: largura automática para acomodar o label */
  width: auto;
  height: 32px;
  padding: 0 0.65rem;
  flex-shrink: 0;
  border: 1px solid var(--danger);
  border-radius: 6px;
  background-color: var(--danger-bg);
  color: var(--danger-text);
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, transform 0.15s;
  white-space: nowrap;
}

.filter-clear:hover {
  background-color: var(--danger-bg);
  transform: scale(1.08);
}

.filter-field-clear {
  flex: 0 0 auto;
  min-width: unset;
  max-width: unset;
}

.clear-icon {
  width: 13px;
  height: 13px;
  flex-shrink: 0;
}
</style>